import { Continent, Country, DataOrigin, DeliveryMethod, FileFormat, FileUnit, Frequency, Industry, ListingCategory, ListingSubCategory, OrganizationType, SellerAccountPricingType, } from "@biggeo/bg-server-lib/studio";
import flatMap from "lodash/flatMap";
import fromPairs from "lodash/fromPairs";
import startCase from "lodash/startCase";
import { match } from "ts-pattern";
const geographicCoverage = {
    [Continent.africa]: [
        Country.Algeria,
        Country.Angola,
        Country.Benin,
        Country.Botswana,
        Country.BurkinaFaso,
        Country.Burundi,
        Country.CaboVerde,
        Country.Cameroon,
        Country.CentralAfricanRepublic,
        Country.Chad,
        Country.Comoros,
        Country.DemocraticRepublicOfTheCongo,
        Country.RepublicOfTheCongo,
        Country.Djibouti,
        Country.Egypt,
        Country.EquatorialGuinea,
        Country.Eritrea,
        Country.Eswatini,
        Country.Ethiopia,
        Country.Gabon,
        Country.Gambia,
        Country.Ghana,
        Country.Guinea,
        Country.GuineaBissau,
        Country.IvoryCoast,
        Country.Kenya,
        Country.Lesotho,
        Country.Liberia,
        Country.Libya,
        Country.Madagascar,
        Country.Malawi,
        Country.Mali,
        Country.Mauritania,
        Country.Mauritius,
        Country.Morocco,
        Country.Mozambique,
        Country.Namibia,
        Country.Niger,
        Country.Nigeria,
        Country.Rwanda,
        Country.SaoTomeAndPrincipe,
        Country.Senegal,
        Country.Seychelles,
        Country.SierraLeone,
        Country.Somalia,
        Country.SouthAfrica,
        Country.SouthSudan,
        Country.Sudan,
        Country.Tanzania,
        Country.Togo,
        Country.Tunisia,
        Country.Uganda,
        Country.Zambia,
        Country.Zimbabwe,
    ],
    [Continent.asia]: [
        Country.Afghanistan,
        Country.Armenia,
        Country.Azerbaijan,
        Country.Bahrain,
        Country.Bangladesh,
        Country.Bhutan,
        Country.Brunei,
        Country.Cambodia,
        Country.China,
        Country.Georgia,
        Country.India,
        Country.Indonesia,
        Country.Iran,
        Country.Iraq,
        Country.Israel,
        Country.Japan,
        Country.Jordan,
        Country.Kazakhstan,
        Country.Kuwait,
        Country.Kyrgyzstan,
        Country.Laos,
        Country.Lebanon,
        Country.Malaysia,
        Country.Maldives,
        Country.Mongolia,
        Country.Myanmar,
        Country.Nepal,
        Country.Oman,
        Country.Pakistan,
        Country.Philippines,
        Country.Qatar,
        Country.SaudiArabia,
        Country.Singapore,
        Country.SouthKorea,
        Country.SriLanka,
        Country.Syria,
        Country.Tajikistan,
        Country.Taiwan,
        Country.Thailand,
        Country.TimorLeste,
        Country.Turkey,
        Country.Turkmenistan,
        Country.UnitedArabEmirates,
        Country.Uzbekistan,
        Country.Vietnam,
        Country.Yemen,
    ],
    [Continent.europe]: [
        Country.Albania,
        Country.Andorra,
        Country.Austria,
        Country.Belarus,
        Country.Belgium,
        Country.BosniaAndHerzegovina,
        Country.Bulgaria,
        Country.Croatia,
        Country.Cyprus,
        Country.CzechRepublic,
        Country.Denmark,
        Country.Estonia,
        Country.Finland,
        Country.France,
        Country.Germany,
        Country.Greece,
        Country.Hungary,
        Country.Iceland,
        Country.Ireland,
        Country.Italy,
        Country.Kosovo,
        Country.Latvia,
        Country.Liechtenstein,
        Country.Lithuania,
        Country.Luxembourg,
        Country.Malta,
        Country.Moldova,
        Country.Monaco,
        Country.Montenegro,
        Country.Netherlands,
        Country.NorthMacedonia,
        Country.Norway,
        Country.Poland,
        Country.Portugal,
        Country.Romania,
        Country.Russia,
        Country.SanMarino,
        Country.Serbia,
        Country.Slovakia,
        Country.Slovenia,
        Country.Spain,
        Country.Sweden,
        Country.Switzerland,
        Country.UnitedKingdom,
        Country.Ukraine,
        Country.VaticanCity,
    ],
    [Continent.northAmerica]: [
        Country.AntiguaAndBarbuda,
        Country.Bahamas,
        Country.Barbados,
        Country.Canada,
        Country.Cuba,
        Country.Dominica,
        Country.DominicanRepublic,
        Country.Grenada,
        Country.Haiti,
        Country.Jamaica,
        Country.Mexico,
        Country.SaintKittsAndNevis,
        Country.SaintLucia,
        Country.SaintVincentAndTheGrenadines,
        Country.TrinidadAndTobago,
        Country.USA,
        Country.PuertoRico,
    ],
    [Continent.centralAmerica]: [
        Country.Belize,
        Country.CostaRica,
        Country.ElSalvador,
        Country.Guatemala,
        Country.Honduras,
        Country.Nicaragua,
        Country.Panama,
    ],
    [Continent.southAmerica]: [
        Country.Argentina,
        Country.Bolivia,
        Country.Brazil,
        Country.Chile,
        Country.Colombia,
        Country.Ecuador,
        Country.Guyana,
        Country.Paraguay,
        Country.Peru,
        Country.Suriname,
        Country.Uruguay,
        Country.Venezuela,
    ],
    [Continent.oceania]: [
        Country.Australia,
        Country.Fiji,
        Country.Kiribati,
        Country.MarshallIslands,
        Country.FederatedStatesOfMicronesia,
        Country.Nauru,
        Country.NewZealand,
        Country.Palau,
        Country.PapuaNewGuinea,
        Country.Samoa,
        Country.SolomonIslands,
        Country.Tonga,
        Country.Tuvalu,
        Country.Vanuatu,
    ],
};
const listingCategories = {
    [ListingCategory.geospatialData]: [
        ListingSubCategory.mobilityData,
        ListingSubCategory.mobileLocationData,
        ListingSubCategory.locationData,
        ListingSubCategory.satelliteData,
        ListingSubCategory.buildingFootprintData,
        ListingSubCategory.pointOfInterestData,
        ListingSubCategory.placesData,
        ListingSubCategory.footTrafficData,
        ListingSubCategory.cellTowerData,
        ListingSubCategory.gisData,
        ListingSubCategory.geographicData,
        ListingSubCategory.geocodingAndAddressData,
        ListingSubCategory.administrativeBoundariesData,
        ListingSubCategory.roadAndStreetNetworkData,
        ListingSubCategory.landUseAndLandCoverData,
    ],
    [ListingCategory.financialData]: [
        ListingSubCategory.stockMarketData,
        ListingSubCategory.fixedIncomeData,
        ListingSubCategory.derivativesAndOtcData,
        ListingSubCategory.commodityData,
        ListingSubCategory.currencyData,
        ListingSubCategory.cryptocurrencyData,
        ListingSubCategory.alternativeData,
        ListingSubCategory.economicData,
        ListingSubCategory.esgData,
        ListingSubCategory.corporateAndBankData,
        ListingSubCategory.fundAndInvestmentHoldingsData,
        ListingSubCategory.creditRatingData,
        ListingSubCategory.insuranceData,
    ],
    [ListingCategory.b2bData]: [
        ListingSubCategory.b2bLeadsData,
        ListingSubCategory.b2bIntentData,
        ListingSubCategory.b2bContactData,
        ListingSubCategory.b2bTransactionData,
        ListingSubCategory.companyData,
        ListingSubCategory.firmographicData,
        ListingSubCategory.technographicData,
    ],
    [ListingCategory.marketingData]: [
        ListingSubCategory.advertisingData,
        ListingSubCategory.audienceBehaviorData,
        ListingSubCategory.consumerMarketingData,
        ListingSubCategory.emailAndSmsEngagementData,
        ListingSubCategory.directMarketingData,
        ListingSubCategory.brandAffinityData,
        ListingSubCategory.realTimeBiddingData,
        ListingSubCategory.healthcareMarketingData,
    ],
    [ListingCategory.commerceData]: [
        ListingSubCategory.retailData,
        ListingSubCategory.ecommerceData,
        ListingSubCategory.onlineMarketplaceData,
        ListingSubCategory.productAndSalesData,
        ListingSubCategory.pointOfSaleData,
        ListingSubCategory.procurementAndSellerData,
        ListingSubCategory.consumerReviewAndConfidenceData,
        ListingSubCategory.businessRegistrationData,
    ],
    [ListingCategory.consumerData]: [
        ListingSubCategory.consumerDemographicData,
        ListingSubCategory.b2cContactData,
        ListingSubCategory.consumerBehaviorData,
        ListingSubCategory.consumerSpendingData,
        ListingSubCategory.consumerPurchaseData,
        ListingSubCategory.consumerTravelData,
        ListingSubCategory.customerExperienceData,
        ListingSubCategory.surveyData,
        ListingSubCategory.consumerSalesTractionData,
        ListingSubCategory.audienceData,
        ListingSubCategory.cpgAndFmcgData,
        ListingSubCategory.kycData,
        ListingSubCategory.productPreferencesAndSentimentData,
        ListingSubCategory.competitiveIntelligenceData,
    ],
    [ListingCategory.webData]: [
        ListingSubCategory.webTrafficData,
        ListingSubCategory.webSearchData,
        ListingSubCategory.webActivityData,
        ListingSubCategory.webScrapingData,
        ListingSubCategory.ipAddressData,
        ListingSubCategory.domainNameData,
        ListingSubCategory.socialMediaData,
        ListingSubCategory.linkedInData,
        ListingSubCategory.linkedInCompanyData,
    ],
    [ListingCategory.realEstateData]: [
        ListingSubCategory.commercialRealEstateData,
        ListingSubCategory.residentialRealEstateData,
        ListingSubCategory.realEstateTransactionData,
        ListingSubCategory.propertyData,
        ListingSubCategory.commercialConstructionData,
        ListingSubCategory.residentialConstructionData,
        ListingSubCategory.commercialLeasingData,
        ListingSubCategory.residentialRentalData,
        ListingSubCategory.shortTermRentalData,
        ListingSubCategory.mortgageAndLoanData,
        ListingSubCategory.foreclosureData,
        ListingSubCategory.parcelData,
        ListingSubCategory.urbanPlanningData,
    ],
    [ListingCategory.aiAndMachineLearningData]: [
        ListingSubCategory.machineLearningData,
        ListingSubCategory.deepLearningData,
        ListingSubCategory.modelTrainingAndValidationSets,
        ListingSubCategory.textualData,
        ListingSubCategory.largeLanguageModelData,
        ListingSubCategory.chatbotTrainingData,
        ListingSubCategory.computerVisionData,
        ListingSubCategory.annotatedImageryData,
        ListingSubCategory.audioData,
        ListingSubCategory.audioAndSpeechRecognitionData,
        ListingSubCategory.syntheticData,
        ListingSubCategory.syntheticDataGeneration,
    ],
    [ListingCategory.populationData]: [
        ListingSubCategory.censusData,
        ListingSubCategory.demographicData,
        ListingSubCategory.geodemographicData,
        ListingSubCategory.spendingData,
        ListingSubCategory.wealthData,
        ListingSubCategory.populationData,
    ],
    [ListingCategory.tradeData]: [
        ListingSubCategory.gdpAndOutputData,
        ListingSubCategory.inflationData,
        ListingSubCategory.interestRateData,
        ListingSubCategory.globalTradeData,
        ListingSubCategory.importExportData,
    ],
    [ListingCategory.weatherAndClimateData]: [
        ListingSubCategory.historicalWeatherData,
        ListingSubCategory.realTimeWeatherData,
        ListingSubCategory.severeWeatherData,
        ListingSubCategory.lightningData,
        ListingSubCategory.climateData,
        ListingSubCategory.windData,
        ListingSubCategory.temperatureData,
        ListingSubCategory.weatherForecastData,
        ListingSubCategory.precipitationData,
        ListingSubCategory.fireData,
        ListingSubCategory.droughtAndFloodData,
        ListingSubCategory.atmosphereData,
        ListingSubCategory.pollenData,
        ListingSubCategory.solarData,
    ],
    [ListingCategory.environmentalData]: [
        ListingSubCategory.waterData,
        ListingSubCategory.wasteData,
        ListingSubCategory.airQualityData,
        ListingSubCategory.noiseData,
        ListingSubCategory.surfaceData,
        ListingSubCategory.soilData,
        ListingSubCategory.geologicalData,
        ListingSubCategory.carbonFootprintData,
        ListingSubCategory.lifeCycleAssessmentData,
        ListingSubCategory.deforestationData,
        ListingSubCategory.wildlifeAndConservationData,
    ],
    [ListingCategory.legalData]: [
        ListingSubCategory.courtCaseRecords,
        ListingSubCategory.courtData,
        ListingSubCategory.litigationData,
        ListingSubCategory.intellectualPropertyData,
        ListingSubCategory.patentData,
        ListingSubCategory.trademarkData,
        ListingSubCategory.copyrightData,
        ListingSubCategory.royaltyRatesData,
        ListingSubCategory.contractData,
        ListingSubCategory.regulatoryData,
        ListingSubCategory.attorneyData,
        ListingSubCategory.legalPartiesData,
    ],
    [ListingCategory.healthcareData]: [
        ListingSubCategory.patientData,
        ListingSubCategory.medicalClaimsData,
        ListingSubCategory.prescriptionDrugData,
        ListingSubCategory.hospitalData,
        ListingSubCategory.publicHealthData,
        ListingSubCategory.pharmaData,
        ListingSubCategory.healthcareProfessionalData,
        ListingSubCategory.medicalDeviceData,
        ListingSubCategory.medicalImageryData,
        ListingSubCategory.labData,
        ListingSubCategory.covid19Data,
        ListingSubCategory.virusData,
        ListingSubCategory.dentalData,
        ListingSubCategory.donorData,
    ],
    [ListingCategory.politicalData]: [
        ListingSubCategory.voterData,
        ListingSubCategory.campaignAndElectionData,
        ListingSubCategory.fundraisingAndDonorData,
        ListingSubCategory.governmentData,
        ListingSubCategory.congressionalData,
        ListingSubCategory.lobbyingData,
        ListingSubCategory.budgetAndSpendingData,
    ],
    [ListingCategory.energyData]: [
        ListingSubCategory.electricityGenerationData,
        ListingSubCategory.oilAndGasData,
        ListingSubCategory.consumptionAndLoadData,
        ListingSubCategory.energyPricingData,
        ListingSubCategory.carbonEmissionsData,
        ListingSubCategory.renewablesData,
        ListingSubCategory.solarEnergyData,
        ListingSubCategory.windPowerData,
        ListingSubCategory.energyInfrastructureData,
        ListingSubCategory.energyStorageData,
        ListingSubCategory.demandForecastingData,
        ListingSubCategory.energyEfficiencyData,
        ListingSubCategory.fuelData,
        ListingSubCategory.hydropowerData,
        ListingSubCategory.energyPolicyAndRegulationData,
    ],
    [ListingCategory.riskData]: [
        ListingSubCategory.crimeRiskData,
        ListingSubCategory.cyberRiskData,
        ListingSubCategory.climateRiskData,
        ListingSubCategory.politicalRiskData,
        ListingSubCategory.supplyChainRiskData,
        ListingSubCategory.fraudRiskData,
        ListingSubCategory.reputationalRiskData,
        ListingSubCategory.esgRiskData,
        ListingSubCategory.insuranceRiskData,
        ListingSubCategory.environmentRiskData,
        ListingSubCategory.healthRiskData,
        ListingSubCategory.waterRiskData,
        ListingSubCategory.humanRightsRiskData,
        ListingSubCategory.publicSafetyMetricsData,
        ListingSubCategory.lawEnforcementActivityData,
        ListingSubCategory.borderAndNationalSecurityData,
    ],
    [ListingCategory.transportationAndLogisticsData]: [
        ListingSubCategory.supplyChainData,
        ListingSubCategory.freightData,
        ListingSubCategory.shipmentData,
        ListingSubCategory.truckingData,
        ListingSubCategory.marineData,
        ListingSubCategory.portData,
        ListingSubCategory.aviationData,
        ListingSubCategory.roadData,
        ListingSubCategory.trafficFlowAndCongestionData,
        ListingSubCategory.routeOptimizationAndEtaData,
        ListingSubCategory.logisticsNetworkData,
        ListingSubCategory.fleetTelematicsData,
        ListingSubCategory.electricVehicleEvFleetData,
        ListingSubCategory.vehicleRegistrationData,
        ListingSubCategory.lastMileDeliveryData,
        ListingSubCategory.thirdPartyLogistics3plProviderData,
        ListingSubCategory.publicTransitData,
        ListingSubCategory.rideshareAndTaxiData,
    ],
    [ListingCategory.agricultureData]: [
        ListingSubCategory.cropData,
        ListingSubCategory.soilAndAgronomicData,
        ListingSubCategory.yieldAndProductionData,
        ListingSubCategory.fertilizerAndChemicalUsageData,
        ListingSubCategory.harvestTimingAndMaturityData,
        ListingSubCategory.pestAndDiseaseData,
        ListingSubCategory.satelliteAndRemoteSensingData,
        ListingSubCategory.environmentalAndClimateData,
        ListingSubCategory.irrigationAndWaterUsageData,
        ListingSubCategory.farmBoundaryData,
        ListingSubCategory.agriculturalMachineryAndEquipmentData,
        ListingSubCategory.livestockData,
        ListingSubCategory.marketAndPricingData,
    ],
    [ListingCategory.telecomData]: [
        ListingSubCategory.mobileNetworkCoverageData,
        ListingSubCategory.mobileNetworkSpeedData,
        ListingSubCategory.fiveGDeploymentData,
        ListingSubCategory.cellTowerLocationData,
        ListingSubCategory.spectrumAllocationData,
        ListingSubCategory.broadbandData,
        ListingSubCategory.fixedNetworkData,
        ListingSubCategory.callAndSmsVolumesData,
        ListingSubCategory.droppedCallAndNetworkOutageData,
        ListingSubCategory.roamingData,
        ListingSubCategory.iotData,
        ListingSubCategory.devicePenetrationData,
        ListingSubCategory.subscriberDemographicAndUsageData,
        ListingSubCategory.mobileAppUsageOverNetworks,
    ],
    [ListingCategory.laborAndEmploymentData]: [
        ListingSubCategory.jobPostingsData,
        ListingSubCategory.salaryData,
        ListingSubCategory.employeeData,
        ListingSubCategory.recruitingData,
        ListingSubCategory.talentData,
        ListingSubCategory.turnoverAndAttritionData,
        ListingSubCategory.remoteAndHybridWorkData,
        ListingSubCategory.workplaceDiversityData,
        ListingSubCategory.skillTaxonomyData,
        ListingSubCategory.benefitsAndPerksData,
        ListingSubCategory.jobMarketData,
        ListingSubCategory.employerRatingAndReviewData,
        ListingSubCategory.workforceLocationAndOfficeSiteData,
        ListingSubCategory.linkedinData,
        ListingSubCategory.glassdoorData,
        ListingSubCategory.indeedData,
        ListingSubCategory.unionMembershipAndLaborActionData,
        ListingSubCategory.laborLawComplianceData,
        ListingSubCategory.humanCapitalData,
    ],
    [ListingCategory.infrastructureData]: [
        ListingSubCategory.roadNetworksData,
        ListingSubCategory.bridgeData,
        ListingSubCategory.utilityData,
        ListingSubCategory.powerGridAndTransmissionLineData,
        ListingSubCategory.waterAndSewageInfrastructureData,
        ListingSubCategory.telecomAndDigitalInfrastructureData,
        ListingSubCategory.railNetworksData,
        ListingSubCategory.publicTransitInfrastructureData,
        ListingSubCategory.airportInfrastructureData,
        ListingSubCategory.constructionPermitsData,
    ],
    [ListingCategory.mediaAndEntertainmentData]: [
        ListingSubCategory.streamingData,
        ListingSubCategory.tvData,
        ListingSubCategory.audienceMeasurementData,
        ListingSubCategory.contentEngagementData,
        ListingSubCategory.gamingAndEsportsData,
        ListingSubCategory.sportsData,
        ListingSubCategory.musicData,
        ListingSubCategory.podcastData,
        ListingSubCategory.influencerAndCreatorEconomyData,
        ListingSubCategory.socialMediaBuzzAndSentimentData,
        ListingSubCategory.mediaTrendAndConsumptionForecastingData,
        ListingSubCategory.newsData,
        ListingSubCategory.eventData,
        ListingSubCategory.contentMetadataAndCatalogData,
        ListingSubCategory.oddsAndBettingData,
    ],
};
export const paymentTypes = [
    SellerAccountPricingType.hotSubscription,
    SellerAccountPricingType.subscription,
    SellerAccountPricingType.oneTime,
];
export const PaymentTypeTooltips = {
    [SellerAccountPricingType.oneTime]: "One-time purchase providing 10 days of access to the dataset.",
    [SellerAccountPricingType.subscription]: "Subscription providing weekly, monthly, or yearly access to the dataset.",
    [SellerAccountPricingType.hotSubscription]: "Always On Subscription providing access to always-ready dataset, high-speed compute resources, and unlimited data egress.",
};
export const PriceTypeLabels = {
    [SellerAccountPricingType.oneTime]: "One-Time Purchase",
    [SellerAccountPricingType.subscription]: "Subscription",
    [SellerAccountPricingType.hotSubscription]: "Always On Subscription",
};
export const isOneTimeSellerAccountPricing = (value) => {
    return value.type === SellerAccountPricingType.oneTime;
};
export const isSubscriptionSellerAccountPricing = (value) => {
    return value.type !== SellerAccountPricingType.oneTime;
};
export const getCategory = (subCategory) => {
    return fromPairs(flatMap(listingCategories, (subcategories, category) => subcategories.map((sub) => [sub, category])))[subCategory];
};
export const getSubCategories = (category) => listingCategories[category];
export const getCountries = (continent) => geographicCoverage[continent];
export const getContinent = (country) => {
    return fromPairs(flatMap(geographicCoverage, (countries, continent) => countries.map((sub) => [sub, continent])))[country];
};
export const getCountryFromString = (country) => {
    return Object.values(Country).find((value) => startCase(value) === country);
};
export const isListingCategory = (value) => {
    return value in ListingCategory;
};
export const getCategorySlug = (category) => category.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
export const getCategoryFromSlug = (slug) => {
    const category = slug.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
    return isListingCategory(category) ? category : undefined;
};
export const getCategoryFromString = (value) => {
    if (!value)
        return undefined;
    const options = Object.values(ListingCategory);
    const category = options.find((option) => option.toLowerCase().includes(value.toLowerCase()));
    return category && isListingCategory(category) ? category : undefined;
};
export const isListingSubCategory = (value) => {
    return value in ListingSubCategory;
};
export const getSubCategorySlug = (subCategory) => subCategory.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
export const getSubCategoryFromSlug = (slug) => {
    const subCategory = slug.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
    return isListingSubCategory(subCategory) ? subCategory : undefined;
};
export const getOrganizationTypeLabel = (organizationType, slim) => match(organizationType)
    .with(OrganizationType.enterprises, () => slim ? "Enterprises" : "Enterprises (500+ employees)")
    .with(OrganizationType.midSizedCompanies, () => slim
    ? "Mid-Sized Companies"
    : "Mid-Sized Companies (50-500 employees)")
    .with(OrganizationType.smallBusinesses, () => slim ? "Small Businesses" : "Small Businesses (10-50 employees)")
    .with(OrganizationType.freelancersAndIndividualUsers, () => slim
    ? "Freelancers & Individual Users"
    : "Freelancers & Individual Users (1-10 employees)")
    .exhaustive();
export const getDataOriginLabel = (dataOrigin) => match(dataOrigin)
    .with(DataOrigin.satelliteImagery, () => "Satellite Imagery")
    .with(DataOrigin.aerialSurveys, () => "Aerial Surveys")
    .with(DataOrigin.iotSensors, () => "IoT Sensors")
    .with(DataOrigin.governmentOpenData, () => "Government Open Data")
    .with(DataOrigin.crowdsourcedData, () => "Crowdsourced Data")
    .with(DataOrigin.webScraping, () => "Web Scraping")
    .with(DataOrigin.mobileGpsData, () => "Mobile GPS Data")
    .with(DataOrigin.enterpriseDatabases, () => "Enterprise Databases")
    .with(DataOrigin.scientificResearch, () => "Scientific Research")
    .with(DataOrigin.thirdParty, () => "Third-party")
    .with(DataOrigin.other, () => "Other")
    .exhaustive();
export const getFrequencyLabel = (frequency) => match(frequency)
    .with(Frequency.realTime, () => "Real-Time")
    .with(Frequency.secondly, () => "Per Second")
    .with(Frequency.minutely, () => "Per Minute")
    .with(Frequency.hourly, () => "Hourly")
    .with(Frequency.daily, () => "Daily")
    .with(Frequency.weekly, () => "Weekly")
    .with(Frequency.monthly, () => "Monthly")
    .with(Frequency.quarterly, () => "Quarterly")
    .with(Frequency.annual, () => "Yearly")
    .with(Frequency.onDemand, () => "On-demand")
    .with(Frequency.never, () => "Never")
    .exhaustive();
export const getFileFormatLabel = (fileFormat) => match(fileFormat)
    .with(FileFormat.csv, () => "CSV")
    .with(FileFormat.parquet, () => "Parquet")
    .with(FileFormat.excel, () => "Excel")
    .with(FileFormat.json, () => "JSON")
    .with(FileFormat.xml, () => "XML")
    .with(FileFormat.sql, () => "SQL")
    .with(FileFormat.binFile, () => "BIN file")
    .with(FileFormat.txt, () => "TXT")
    .with(FileFormat.biggeoIndex, () => "BigGeo Index")
    .with(FileFormat.other, () => "Other")
    .exhaustive();
export const getFileUnitLabel = (fileUnit) => match(fileUnit)
    .with(FileUnit.B, () => "Byte")
    .with(FileUnit.KB, () => "Kilobyte")
    .with(FileUnit.MB, () => "Megabyte")
    .with(FileUnit.GB, () => "Gigabyte")
    .with(FileUnit.TB, () => "Terabyte")
    .with(FileUnit.PB, () => "Petabyte")
    .with(FileUnit.EB, () => "Exabyte")
    .with(FileUnit.ZB, () => "Zettabyte")
    .with(FileUnit.YB, () => "Yottabyte")
    .exhaustive();
export const getDeliveryMethodLabel = (deliveryMethod) => match(deliveryMethod)
    .with(DeliveryMethod.amazonS3Bucket, () => "Amazon S3 Bucket")
    .with(DeliveryMethod.azureBlobStorage, () => "Azure Blob Storage")
    .with(DeliveryMethod.googleCloudStorage, () => "Google Cloud Storage (GCS)")
    .with(DeliveryMethod.email, () => "Email")
    .with(DeliveryMethod.restApi, () => "REST API")
    .with(DeliveryMethod.download, () => "Download")
    .with(DeliveryMethod.sftp, () => "SFTP")
    .with(DeliveryMethod.feedApi, () => "Feed API")
    .with(DeliveryMethod.streamingApi, () => "Streaming API")
    .with(DeliveryMethod.soapApi, () => "SOAP API")
    .with(DeliveryMethod.datascape, () => "Datascape")
    .with(DeliveryMethod.other, () => "Other")
    .exhaustive();
export const getIndustryLabel = (industry) => match(industry)
    .with(Industry.CommerceAndIndustry, () => "Commerce And Industry")
    .with(Industry.EnergyAndUtility, () => "Energy And Utility")
    .with(Industry.EnvironmentalAndLandManagement, () => "Environmental And Land Management")
    .with(Industry.HealthcareAndInsurance, () => "Healthcare And Insurance")
    .with(Industry.InfrastructureAndConstruction, () => "Infrastructure And Construction")
    .with(Industry.PublicSectorAndGovernment, () => "Public Sector And Government")
    .with(Industry.TechnologyAndSecurity, () => "Technology And Security")
    .with(Industry.TourismAndTravel, () => "Tourism And Travel")
    .with(Industry.WeatherAndClimate, () => "Weather And Climate")
    .exhaustive();
export const getCategoryLabel = (category) => match(category)
    .with(ListingCategory.geospatialData, () => "Geospatial Data")
    .with(ListingCategory.financialData, () => "Financial Data")
    .with(ListingCategory.b2bData, () => "B2B Data")
    .with(ListingCategory.marketingData, () => "Marketing Data")
    .with(ListingCategory.commerceData, () => "Commerce Data")
    .with(ListingCategory.consumerData, () => "Consumer Data")
    .with(ListingCategory.webData, () => "Web Data")
    .with(ListingCategory.realEstateData, () => "Real Estate Data")
    .with(ListingCategory.aiAndMachineLearningData, () => "AI & Machine Learning Data")
    .with(ListingCategory.populationData, () => "Population Data")
    .with(ListingCategory.tradeData, () => "Trade Data")
    .with(ListingCategory.weatherAndClimateData, () => "Weather & Climate Data")
    .with(ListingCategory.environmentalData, () => "Environmental Data")
    .with(ListingCategory.legalData, () => "Legal Data")
    .with(ListingCategory.healthcareData, () => "Healthcare Data")
    .with(ListingCategory.politicalData, () => "Political Data")
    .with(ListingCategory.energyData, () => "Energy Data")
    .with(ListingCategory.riskData, () => "Risk Data")
    .with(ListingCategory.transportationAndLogisticsData, () => "Transportation & Logistics Data")
    .with(ListingCategory.agricultureData, () => "Agriculture Data")
    .with(ListingCategory.telecomData, () => "Telecom Data")
    .with(ListingCategory.laborAndEmploymentData, () => "Labor & Employment Data")
    .with(ListingCategory.infrastructureData, () => "Infrastructure Data")
    .with(ListingCategory.mediaAndEntertainmentData, () => "Media & Entertainment Data")
    .exhaustive();
export const getCategoryEnumFromLabel = (label) => match(label)
    .with("Geospatial Data", () => ListingCategory.geospatialData)
    .with("Financial Data", () => ListingCategory.financialData)
    .with("B2B Data", () => ListingCategory.b2bData)
    .with("Marketing Data", () => ListingCategory.marketingData)
    .with("Commerce Data", () => ListingCategory.commerceData)
    .with("Consumer Data", () => ListingCategory.consumerData)
    .with("Web Data", () => ListingCategory.webData)
    .with("Real Estate Data", () => ListingCategory.realEstateData)
    .with("AI & Machine Learning Data", () => ListingCategory.aiAndMachineLearningData)
    .with("Population Data", () => ListingCategory.populationData)
    .with("Trade Data", () => ListingCategory.tradeData)
    .with("Weather & Climate Data", () => ListingCategory.weatherAndClimateData)
    .with("Environmental Data", () => ListingCategory.environmentalData)
    .with("Legal Data", () => ListingCategory.legalData)
    .with("Healthcare Data", () => ListingCategory.healthcareData)
    .with("Political Data", () => ListingCategory.politicalData)
    .with("Energy Data", () => ListingCategory.energyData)
    .with("Risk Data", () => ListingCategory.riskData)
    .with("Transportation & Logistics Data", () => ListingCategory.transportationAndLogisticsData)
    .with("Agriculture Data", () => ListingCategory.agricultureData)
    .with("Telecom Data", () => ListingCategory.telecomData)
    .with("Labor & Employment Data", () => ListingCategory.laborAndEmploymentData)
    .with("Infrastructure Data", () => ListingCategory.infrastructureData)
    .with("Media & Entertainment Data", () => ListingCategory.mediaAndEntertainmentData)
    .otherwise(() => undefined);
export const getCategoryDescription = (category) => match(category)
    .with(ListingCategory.geospatialData, () => "A geospatial dataset contains location-based information such as satellite imagery, mobile signals, POIs, and map layers used to analyze movement, geography, infrastructure, and spatial relationships across physical environments.")
    .with(ListingCategory.financialData, () => "A financial dataset encompasses structured information across markets and institutions including economic indicators, asset prices, ESG metrics, alternative sources, and company-level fundamentals used to analyze, forecast, and manage financial risk, performance, and investment decisions.")
    .with(ListingCategory.b2bData, () => "A B2B dataset includes detailed information on businesses, their contacts, technologies, behaviors, and transactions that helps sales, marketing, and strategy teams identify, target, and engage the right companies at the right time.")
    .with(ListingCategory.marketingData, () => "A marketing dataset includes data on campaigns, consumer interactions, brand perception, and audience behavior across channels used to optimize targeting, personalize messaging, and measure engagement and advertising effectiveness.")
    .with(ListingCategory.commerceData, () => "A commerce dataset captures transactions, behaviors, and entities across retail and ecommerce ecosystems, including products, sellers, sales performance, consumer sentiment, and procurement activity to inform market analysis and business strategy.")
    .with(ListingCategory.consumerData, () => "A consumer dataset includes information on individual behaviors, demographics, purchases, preferences, and interactions across channels, helping businesses understand, segment, and engage their target audiences more effectively.")
    .with(ListingCategory.webData, () => "A web dataset includes data from online sources such as web traffic, search activity, social media, IP addresses, and scraped content used to analyze digital behavior, online presence, and market signals across the internet.")
    .with(ListingCategory.realEstateData, () => "A real estate dataset includes information on properties, transactions, construction, rentals, and financing across residential and commercial sectors used to assess market trends, investment opportunities, and urban development.")
    .with(ListingCategory.aiAndMachineLearningData, () => "An AI and machine learning dataset includes structured and unstructured data such as text, images, audio, and synthetic content used to train, validate, and optimize algorithms across applications like natural language processing, computer vision, and speech recognition.")
    .with(ListingCategory.populationData, () => "A population dataset includes demographic, geographic, and economic characteristics such as age, income, wealth, and household composition used to analyze population trends, segment audiences, and support policy and business decisions.")
    .with(ListingCategory.tradeData, () => "A trade and macroeconomic dataset includes national and international indicators such as GDP, inflation, interest rates, and import/export flows used to assess economic performance, policy impact, and global market dynamics.")
    .with(ListingCategory.weatherAndClimateData, () => "A weather and climate dataset includes historical, real-time, and forecasted data on atmospheric conditions such as temperature, wind, precipitation, and extreme events used to monitor environmental changes.")
    .with(ListingCategory.environmentalData, () => "An environmental dataset includes data on natural resources, pollution, ecosystems, and human impact such as air quality, water use, soil conditions, and carbon emissions used to track environmental health and support sustainability efforts.")
    .with(ListingCategory.legalData, () => "A legal dataset includes information on court cases, intellectual property, regulations, contracts, and legal entities used to support legal research, compliance, rights management, and litigation analysis.")
    .with(ListingCategory.healthcareData, () => "A healthcare dataset includes clinical, operational, and administrative information such as patient records, provider data, medical claims, and pharmaceutical details used to enhance care delivery, public health monitoring, and medical research.")
    .with(ListingCategory.politicalData, () => "A political dataset includes information on voters, elections, campaigns, government activity, and public funding used to analyze political behavior, track legislative trends, and inform civic engagement and policymaking.")
    .with(ListingCategory.energyData, () => "An energy dataset includes data on production, consumption, pricing, and sources such as oil, gas, solar, and wind used to monitor energy markets, forecast demand, and support sustainability and infrastructure planning.")
    .with(ListingCategory.riskData, () => "A risk dataset includes information on threats and vulnerabilities such as crime, climate, political instability, cybersecurity, and public safety used to assess exposure, guide decision-making, and enhance security and resilience strategies.")
    .with(ListingCategory.transportationAndLogisticsData, () => "A transportation and logistics dataset includes data on the movement of goods and people across modes such as freight, aviation, marine, and road transport used to optimize supply chains, monitor traffic, and improve mobility planning.")
    .with(ListingCategory.agricultureData, () => "An agriculture dataset includes data on crops, soil, livestock, climate, and satellite imagery used to enhance farming practices, monitor productivity, manage risks, and support agricultural market decisions.")
    .with(ListingCategory.telecomData, () => "A telecom dataset includes information on network coverage, infrastructure, connectivity performance, and communication volumes used to analyze service quality, expand infrastructure, and support digital and IoT applications.")
    .with(ListingCategory.laborAndEmploymentData, () => "A labor and employment dataset includes data on jobs, salaries, workforce demographics, skills, and recruitment activity used to analyze labor markets, track employment trends, and support workforce planning and talent management.")
    .with(ListingCategory.infrastructureData, () => "An infrastructure dataset includes information on physical and digital assets such as roads, bridges, utilities, and construction activity used to assess development, support urban planning, and manage public and private infrastructure systems.")
    .with(ListingCategory.mediaAndEntertainmentData, () => "A media and entertainment dataset includes data on content consumption, advertising, sports, gaming, and events used to analyze audience engagement, track trends, and support content strategy and monetization.")
    .exhaustive();
export const getSubcategoryLabel = (subcategory) => match(subcategory)
    .with(ListingSubCategory.mobilityData, () => "Mobility Data")
    .with(ListingSubCategory.mobileLocationData, () => "Mobile Location Data")
    .with(ListingSubCategory.locationData, () => "Location Data")
    .with(ListingSubCategory.satelliteData, () => "Satellite Data")
    .with(ListingSubCategory.buildingFootprintData, () => "Building Footprint Data")
    .with(ListingSubCategory.pointOfInterestData, () => "Point of Interest (POI) Data")
    .with(ListingSubCategory.placesData, () => "Places Data")
    .with(ListingSubCategory.footTrafficData, () => "Foot Traffic Data")
    .with(ListingSubCategory.cellTowerData, () => "Cell Tower Data")
    .with(ListingSubCategory.gisData, () => "GIS Data")
    .with(ListingSubCategory.geographicData, () => "Geographic Data")
    .with(ListingSubCategory.geocodingAndAddressData, () => "Geocoding & Address Data")
    .with(ListingSubCategory.administrativeBoundariesData, () => "Administrative Boundaries Data")
    .with(ListingSubCategory.roadAndStreetNetworkData, () => "Road and Street Network Data")
    .with(ListingSubCategory.landUseAndLandCoverData, () => "Land Use & Land Cover Data")
    .with(ListingSubCategory.stockMarketData, () => "Stock Market Data")
    .with(ListingSubCategory.fixedIncomeData, () => "Fixed Income Data")
    .with(ListingSubCategory.derivativesAndOtcData, () => "Derivatives & OTC Data")
    .with(ListingSubCategory.commodityData, () => "Commodity Data")
    .with(ListingSubCategory.currencyData, () => "Currency Data")
    .with(ListingSubCategory.cryptocurrencyData, () => "Cryptocurrency Data")
    .with(ListingSubCategory.alternativeData, () => "Alternative Data")
    .with(ListingSubCategory.economicData, () => "Economic Data")
    .with(ListingSubCategory.esgData, () => "ESG Data")
    .with(ListingSubCategory.corporateAndBankData, () => "Corporate & Bank Data")
    .with(ListingSubCategory.fundAndInvestmentHoldingsData, () => "Fund & Investment Holdings Data")
    .with(ListingSubCategory.creditRatingData, () => "Credit Rating Data")
    .with(ListingSubCategory.insuranceData, () => "Insurance Data")
    .with(ListingSubCategory.b2bLeadsData, () => "B2B Leads Data")
    .with(ListingSubCategory.b2bIntentData, () => "B2B Intent Data")
    .with(ListingSubCategory.b2bContactData, () => "B2B Contact Data")
    .with(ListingSubCategory.b2bTransactionData, () => "B2B Transaction Data")
    .with(ListingSubCategory.companyData, () => "Company Data")
    .with(ListingSubCategory.firmographicData, () => "Firmographic Data")
    .with(ListingSubCategory.technographicData, () => "Technographic Data")
    .with(ListingSubCategory.advertisingData, () => "Advertising Data")
    .with(ListingSubCategory.audienceBehaviorData, () => "Audience Behavior Data")
    .with(ListingSubCategory.consumerMarketingData, () => "Consumer Marketing Data")
    .with(ListingSubCategory.emailAndSmsEngagementData, () => "Email and SMS Engagement Data")
    .with(ListingSubCategory.directMarketingData, () => "Direct Marketing Data")
    .with(ListingSubCategory.brandAffinityData, () => "Brand & Affinity Data")
    .with(ListingSubCategory.realTimeBiddingData, () => "Real-Time Bidding (RTB) Data")
    .with(ListingSubCategory.healthcareMarketingData, () => "Healthcare Marketing Data")
    .with(ListingSubCategory.retailData, () => "Retail Data")
    .with(ListingSubCategory.ecommerceData, () => "Ecommerce Data")
    .with(ListingSubCategory.onlineMarketplaceData, () => "Online Marketplace Data")
    .with(ListingSubCategory.productAndSalesData, () => "Product & Sales Data")
    .with(ListingSubCategory.pointOfSaleData, () => "Point-of-Sale (POS) Data")
    .with(ListingSubCategory.procurementAndSellerData, () => "Procurement & Seller Data")
    .with(ListingSubCategory.consumerReviewAndConfidenceData, () => "Consumer Review & Confidence Data")
    .with(ListingSubCategory.businessRegistrationData, () => "Business Registration Data")
    .with(ListingSubCategory.consumerDemographicData, () => "Consumer Demographic Data")
    .with(ListingSubCategory.b2cContactData, () => "B2C Contact Data")
    .with(ListingSubCategory.consumerBehaviorData, () => "Consumer Behavior Data")
    .with(ListingSubCategory.consumerSpendingData, () => "Consumer Spending Data")
    .with(ListingSubCategory.consumerPurchaseData, () => "Consumer Purchase Data")
    .with(ListingSubCategory.consumerTravelData, () => "Consumer Travel Data")
    .with(ListingSubCategory.customerExperienceData, () => "Customer Experience Data")
    .with(ListingSubCategory.surveyData, () => "Survey Data")
    .with(ListingSubCategory.consumerSalesTractionData, () => "Consumer Sales Traction Data")
    .with(ListingSubCategory.audienceData, () => "Audience Data")
    .with(ListingSubCategory.cpgAndFmcgData, () => "CPG & FMCG Data")
    .with(ListingSubCategory.kycData, () => "KYC Data")
    .with(ListingSubCategory.productPreferencesAndSentimentData, () => "Product Preferences & Sentiment Data")
    .with(ListingSubCategory.competitiveIntelligenceData, () => "Competitive Intelligence Data")
    .with(ListingSubCategory.webTrafficData, () => "Web Traffic Data")
    .with(ListingSubCategory.webSearchData, () => "Web Search Data")
    .with(ListingSubCategory.webActivityData, () => "Web Activity Data")
    .with(ListingSubCategory.webScrapingData, () => "Web Scraping Data")
    .with(ListingSubCategory.ipAddressData, () => "IP Address Data")
    .with(ListingSubCategory.domainNameData, () => "Domain Name Data")
    .with(ListingSubCategory.socialMediaData, () => "Social Media Data")
    .with(ListingSubCategory.linkedInData, () => "LinkedIn Data")
    .with(ListingSubCategory.linkedInCompanyData, () => "LinkedIn Company Data")
    .with(ListingSubCategory.commercialRealEstateData, () => "Commercial Real Estate Data")
    .with(ListingSubCategory.residentialRealEstateData, () => "Residential Real Estate Data")
    .with(ListingSubCategory.realEstateTransactionData, () => "Real Estate Transaction Data")
    .with(ListingSubCategory.propertyData, () => "Property Data")
    .with(ListingSubCategory.commercialConstructionData, () => "Commercial Construction Data")
    .with(ListingSubCategory.residentialConstructionData, () => "Residential Construction Data")
    .with(ListingSubCategory.commercialLeasingData, () => "Commercial Leasing Data")
    .with(ListingSubCategory.residentialRentalData, () => "Residential Rental Data")
    .with(ListingSubCategory.shortTermRentalData, () => "Short Term Rental Data")
    .with(ListingSubCategory.mortgageAndLoanData, () => "Mortgage and Loan Data")
    .with(ListingSubCategory.foreclosureData, () => "Foreclosure Data")
    .with(ListingSubCategory.parcelData, () => "Parcel Data")
    .with(ListingSubCategory.urbanPlanningData, () => "Urban Planning Data")
    .with(ListingSubCategory.machineLearningData, () => "Machine Learning (ML) Data")
    .with(ListingSubCategory.deepLearningData, () => "Deep Learning (DL) Data")
    .with(ListingSubCategory.modelTrainingAndValidationSets, () => "Model Training and Validation Sets")
    .with(ListingSubCategory.textualData, () => "Textual Data")
    .with(ListingSubCategory.largeLanguageModelData, () => "Large Language Model (LLM) Data")
    .with(ListingSubCategory.chatbotTrainingData, () => "Chatbot Training Data")
    .with(ListingSubCategory.computerVisionData, () => "Computer Vision Data")
    .with(ListingSubCategory.annotatedImageryData, () => "Annotated Imagery Data")
    .with(ListingSubCategory.audioData, () => "Audio Data")
    .with(ListingSubCategory.audioAndSpeechRecognitionData, () => "Audio and Speech Recognition Data")
    .with(ListingSubCategory.syntheticData, () => "Synthetic Data")
    .with(ListingSubCategory.syntheticDataGeneration, () => "Synthetic Data Generation")
    .with(ListingSubCategory.censusData, () => "Census Data")
    .with(ListingSubCategory.demographicData, () => "Demographic Data")
    .with(ListingSubCategory.geodemographicData, () => "Geodemographic Data")
    .with(ListingSubCategory.spendingData, () => "Spending Data")
    .with(ListingSubCategory.wealthData, () => "Wealth Data")
    .with(ListingSubCategory.populationData, () => "Population Data")
    .with(ListingSubCategory.gdpAndOutputData, () => "GDP and Output Data")
    .with(ListingSubCategory.inflationData, () => "Inflation Data")
    .with(ListingSubCategory.interestRateData, () => "Interest Rate Data")
    .with(ListingSubCategory.globalTradeData, () => "Global Trade Data")
    .with(ListingSubCategory.importExportData, () => "Import/Export Data")
    .with(ListingSubCategory.historicalWeatherData, () => "Historical Weather Data")
    .with(ListingSubCategory.realTimeWeatherData, () => "Real-Time Weather Data")
    .with(ListingSubCategory.severeWeatherData, () => "Severe Weather Data")
    .with(ListingSubCategory.lightningData, () => "Lightning Data")
    .with(ListingSubCategory.climateData, () => "Climate Data")
    .with(ListingSubCategory.windData, () => "Wind Data")
    .with(ListingSubCategory.temperatureData, () => "Temperature Data")
    .with(ListingSubCategory.weatherForecastData, () => "Weather Forecast Data")
    .with(ListingSubCategory.precipitationData, () => "Precipitation Data")
    .with(ListingSubCategory.fireData, () => "Fire Data")
    .with(ListingSubCategory.droughtAndFloodData, () => "Drought and Flood Data")
    .with(ListingSubCategory.atmosphereData, () => "Atmosphere Data")
    .with(ListingSubCategory.pollenData, () => "Pollen Data")
    .with(ListingSubCategory.solarData, () => "Solar Data")
    .with(ListingSubCategory.waterData, () => "Water Data")
    .with(ListingSubCategory.wasteData, () => "Waste Data")
    .with(ListingSubCategory.airQualityData, () => "Air Quality Data")
    .with(ListingSubCategory.noiseData, () => "Noise Data")
    .with(ListingSubCategory.surfaceData, () => "Surface Data")
    .with(ListingSubCategory.soilData, () => "Soil Data")
    .with(ListingSubCategory.geologicalData, () => "Geological Data")
    .with(ListingSubCategory.carbonFootprintData, () => "Carbon Footprint Data")
    .with(ListingSubCategory.lifeCycleAssessmentData, () => "Life Cycle Assessment (LCA) Data")
    .with(ListingSubCategory.deforestationData, () => "Deforestation Data")
    .with(ListingSubCategory.wildlifeAndConservationData, () => "Wildlife and Conservation Data")
    .with(ListingSubCategory.courtCaseRecords, () => "Court Case Records")
    .with(ListingSubCategory.courtData, () => "Court Data")
    .with(ListingSubCategory.litigationData, () => "Litigation Data")
    .with(ListingSubCategory.intellectualPropertyData, () => "Intellectual Property Data")
    .with(ListingSubCategory.patentData, () => "Patent Data")
    .with(ListingSubCategory.trademarkData, () => "Trademark Data")
    .with(ListingSubCategory.copyrightData, () => "Copyright Data")
    .with(ListingSubCategory.royaltyRatesData, () => "Royalty Rates Data")
    .with(ListingSubCategory.contractData, () => "Contract Data")
    .with(ListingSubCategory.regulatoryData, () => "Regulatory Data")
    .with(ListingSubCategory.attorneyData, () => "Attorney Data")
    .with(ListingSubCategory.legalPartiesData, () => "Legal Parties Data")
    .with(ListingSubCategory.patientData, () => "Patient Data")
    .with(ListingSubCategory.medicalClaimsData, () => "Medical Claims Data")
    .with(ListingSubCategory.prescriptionDrugData, () => "Prescription Drug Data")
    .with(ListingSubCategory.hospitalData, () => "Hospital Data")
    .with(ListingSubCategory.publicHealthData, () => "Public Health Data")
    .with(ListingSubCategory.pharmaData, () => "Pharma Data")
    .with(ListingSubCategory.healthcareProfessionalData, () => "Healthcare Professional Data")
    .with(ListingSubCategory.medicalDeviceData, () => "Medical Device Data")
    .with(ListingSubCategory.medicalImageryData, () => "Medical Imagery Data")
    .with(ListingSubCategory.labData, () => "Lab Data")
    .with(ListingSubCategory.covid19Data, () => "COVID-19 Data")
    .with(ListingSubCategory.virusData, () => "Virus Data")
    .with(ListingSubCategory.dentalData, () => "Dental Data")
    .with(ListingSubCategory.donorData, () => "Donor Data")
    .with(ListingSubCategory.voterData, () => "Voter Data")
    .with(ListingSubCategory.campaignAndElectionData, () => "Campaign and Election Data")
    .with(ListingSubCategory.fundraisingAndDonorData, () => "Fundraising and Donor Data")
    .with(ListingSubCategory.governmentData, () => "Government Data")
    .with(ListingSubCategory.congressionalData, () => "Congressional Data")
    .with(ListingSubCategory.lobbyingData, () => "Lobbying Data")
    .with(ListingSubCategory.budgetAndSpendingData, () => "Budget and Spending Data")
    .with(ListingSubCategory.electricityGenerationData, () => "Electricity Generation Data")
    .with(ListingSubCategory.oilAndGasData, () => "Oil and Gas Data")
    .with(ListingSubCategory.consumptionAndLoadData, () => "Consumption and Load Data")
    .with(ListingSubCategory.energyPricingData, () => "Energy Pricing Data")
    .with(ListingSubCategory.carbonEmissionsData, () => "Carbon Emissions Data")
    .with(ListingSubCategory.renewablesData, () => "Renewables Data")
    .with(ListingSubCategory.solarEnergyData, () => "Solar Energy Data")
    .with(ListingSubCategory.windPowerData, () => "Wind Power Data")
    .with(ListingSubCategory.energyInfrastructureData, () => "Energy Infrastructure Data")
    .with(ListingSubCategory.energyStorageData, () => "Energy Storage Data")
    .with(ListingSubCategory.demandForecastingData, () => "Demand Forecasting Data")
    .with(ListingSubCategory.energyEfficiencyData, () => "Energy Efficiency Data")
    .with(ListingSubCategory.fuelData, () => "Fuel Data")
    .with(ListingSubCategory.hydropowerData, () => "Hydropower Data")
    .with(ListingSubCategory.energyPolicyAndRegulationData, () => "Energy Policy & Regulation Data")
    .with(ListingSubCategory.crimeRiskData, () => "Crime Risk Data")
    .with(ListingSubCategory.cyberRiskData, () => "Cyber Risk Data")
    .with(ListingSubCategory.climateRiskData, () => "Climate Risk Data")
    .with(ListingSubCategory.politicalRiskData, () => "Political Risk Data")
    .with(ListingSubCategory.supplyChainRiskData, () => "Supply Chain Risk Data")
    .with(ListingSubCategory.fraudRiskData, () => "Fraud Risk Data")
    .with(ListingSubCategory.reputationalRiskData, () => "Reputational Risk Data")
    .with(ListingSubCategory.esgRiskData, () => "ESG Risk Data")
    .with(ListingSubCategory.insuranceRiskData, () => "Insurance Risk Data")
    .with(ListingSubCategory.environmentRiskData, () => "Environment Risk Data")
    .with(ListingSubCategory.healthRiskData, () => "Health Risk Data")
    .with(ListingSubCategory.waterRiskData, () => "Water Risk Data")
    .with(ListingSubCategory.humanRightsRiskData, () => "Human Rights Risk Data")
    .with(ListingSubCategory.publicSafetyMetricsData, () => "Public Safety Metrics Data")
    .with(ListingSubCategory.lawEnforcementActivityData, () => "Law Enforcement Activity Data")
    .with(ListingSubCategory.borderAndNationalSecurityData, () => "Border and National Security Data")
    .with(ListingSubCategory.supplyChainData, () => "Supply Chain Data")
    .with(ListingSubCategory.freightData, () => "Freight Data")
    .with(ListingSubCategory.shipmentData, () => "Shipment Data")
    .with(ListingSubCategory.truckingData, () => "Trucking Data")
    .with(ListingSubCategory.marineData, () => "Marine Data")
    .with(ListingSubCategory.portData, () => "Port Data")
    .with(ListingSubCategory.aviationData, () => "Aviation Data")
    .with(ListingSubCategory.roadData, () => "Road Data")
    .with(ListingSubCategory.trafficFlowAndCongestionData, () => "Traffic Flow & Congestion Data")
    .with(ListingSubCategory.routeOptimizationAndEtaData, () => "Route Optimization & ETA Data")
    .with(ListingSubCategory.logisticsNetworkData, () => "Logistics Network Data")
    .with(ListingSubCategory.fleetTelematicsData, () => "Fleet Telematics Data")
    .with(ListingSubCategory.electricVehicleEvFleetData, () => "Electric Vehicle (EV) Fleet Data")
    .with(ListingSubCategory.vehicleRegistrationData, () => "Vehicle Registration Data")
    .with(ListingSubCategory.lastMileDeliveryData, () => "Last-Mile Delivery Data")
    .with(ListingSubCategory.thirdPartyLogistics3plProviderData, () => "Third-Party Logistics (3PL) Provider Data")
    .with(ListingSubCategory.publicTransitData, () => "Public Transit Data")
    .with(ListingSubCategory.rideshareAndTaxiData, () => "Rideshare and Taxi Data")
    .with(ListingSubCategory.cropData, () => "Crop Data")
    .with(ListingSubCategory.soilAndAgronomicData, () => "Soil & Agronomic Data")
    .with(ListingSubCategory.yieldAndProductionData, () => "Yield & Production Data")
    .with(ListingSubCategory.fertilizerAndChemicalUsageData, () => "Fertilizer & Chemical Usage Data")
    .with(ListingSubCategory.harvestTimingAndMaturityData, () => "Harvest Timing & Maturity Data")
    .with(ListingSubCategory.pestAndDiseaseData, () => "Pest & Disease Data")
    .with(ListingSubCategory.satelliteAndRemoteSensingData, () => "Satellite & Remote Sensing Data")
    .with(ListingSubCategory.environmentalAndClimateData, () => "Environmental & Climate Data")
    .with(ListingSubCategory.irrigationAndWaterUsageData, () => "Irrigation & Water Usage Data")
    .with(ListingSubCategory.farmBoundaryData, () => "Farm Boundary Data")
    .with(ListingSubCategory.agriculturalMachineryAndEquipmentData, () => "Agricultural Machinery & Equipment Data")
    .with(ListingSubCategory.livestockData, () => "Livestock Data")
    .with(ListingSubCategory.marketAndPricingData, () => "Market & Pricing Data")
    .with(ListingSubCategory.mobileNetworkCoverageData, () => "Mobile Network Coverage Data")
    .with(ListingSubCategory.mobileNetworkSpeedData, () => "Mobile Network Speed Data")
    .with(ListingSubCategory.fiveGDeploymentData, () => "5G Deployment Data")
    .with(ListingSubCategory.cellTowerLocationData, () => "Cell Tower Location Data")
    .with(ListingSubCategory.spectrumAllocationData, () => "Spectrum Allocation Data")
    .with(ListingSubCategory.broadbandData, () => "Broadband Data")
    .with(ListingSubCategory.fixedNetworkData, () => "Fixed Network Data")
    .with(ListingSubCategory.callAndSmsVolumesData, () => "Call and SMS Volumes Data")
    .with(ListingSubCategory.droppedCallAndNetworkOutageData, () => "Dropped Call & Network Outage Data")
    .with(ListingSubCategory.roamingData, () => "Roaming Data")
    .with(ListingSubCategory.iotData, () => "IoT Data")
    .with(ListingSubCategory.devicePenetrationData, () => "Device Penetration Data")
    .with(ListingSubCategory.subscriberDemographicAndUsageData, () => "Subscriber Demographic & Usage Data")
    .with(ListingSubCategory.mobileAppUsageOverNetworks, () => "Mobile App Usage Over Networks")
    .with(ListingSubCategory.jobPostingsData, () => "Job Postings Data")
    .with(ListingSubCategory.salaryData, () => "Salary Data")
    .with(ListingSubCategory.employeeData, () => "Employee Data")
    .with(ListingSubCategory.recruitingData, () => "Recruiting Data")
    .with(ListingSubCategory.talentData, () => "Talent Data")
    .with(ListingSubCategory.turnoverAndAttritionData, () => "Turnover & Attrition Data")
    .with(ListingSubCategory.remoteAndHybridWorkData, () => "Remote & Hybrid Work Data")
    .with(ListingSubCategory.workplaceDiversityData, () => "Workplace Diversity Data")
    .with(ListingSubCategory.skillTaxonomyData, () => "Skill Taxonomy Data")
    .with(ListingSubCategory.benefitsAndPerksData, () => "Benefits & Perks Data")
    .with(ListingSubCategory.jobMarketData, () => "Job Market Data")
    .with(ListingSubCategory.employerRatingAndReviewData, () => "Employer Rating & Review Data")
    .with(ListingSubCategory.workforceLocationAndOfficeSiteData, () => "Workforce Location & Office Site Data")
    .with(ListingSubCategory.linkedinData, () => "LinkedIn Data")
    .with(ListingSubCategory.glassdoorData, () => "Glassdoor Data")
    .with(ListingSubCategory.indeedData, () => "Indeed Data")
    .with(ListingSubCategory.unionMembershipAndLaborActionData, () => "Union Membership & Labor Action Data")
    .with(ListingSubCategory.laborLawComplianceData, () => "Labor Law Compliance Data")
    .with(ListingSubCategory.humanCapitalData, () => "Human Capital Data")
    .with(ListingSubCategory.roadNetworksData, () => "Road Networks Data")
    .with(ListingSubCategory.bridgeData, () => "Bridge Data")
    .with(ListingSubCategory.utilityData, () => "Utility Data")
    .with(ListingSubCategory.powerGridAndTransmissionLineData, () => "Power Grid & Transmission Line Data")
    .with(ListingSubCategory.waterAndSewageInfrastructureData, () => "Water & Sewage Infrastructure Data")
    .with(ListingSubCategory.telecomAndDigitalInfrastructureData, () => "Telecom & Digital Infrastructure Data")
    .with(ListingSubCategory.railNetworksData, () => "Rail Networks Data")
    .with(ListingSubCategory.publicTransitInfrastructureData, () => "Public Transit Infrastructure Data")
    .with(ListingSubCategory.airportInfrastructureData, () => "Airport Infrastructure Data")
    .with(ListingSubCategory.constructionPermitsData, () => "Construction Permits Data")
    .with(ListingSubCategory.streamingData, () => "Streaming Data")
    .with(ListingSubCategory.tvData, () => "TV Data")
    .with(ListingSubCategory.audienceMeasurementData, () => "Audience Measurement Data")
    .with(ListingSubCategory.contentEngagementData, () => "Content Engagement Data")
    .with(ListingSubCategory.gamingAndEsportsData, () => "Gaming & Esports Data")
    .with(ListingSubCategory.sportsData, () => "Sports Data")
    .with(ListingSubCategory.musicData, () => "Music Data")
    .with(ListingSubCategory.podcastData, () => "Podcast Data")
    .with(ListingSubCategory.influencerAndCreatorEconomyData, () => "Influencer & Creator Economy Data")
    .with(ListingSubCategory.socialMediaBuzzAndSentimentData, () => "Social Media Buzz & Sentiment Data")
    .with(ListingSubCategory.mediaTrendAndConsumptionForecastingData, () => "Media Trend & Consumption Forecasting Data")
    .with(ListingSubCategory.newsData, () => "News Data")
    .with(ListingSubCategory.eventData, () => "Event Data")
    .with(ListingSubCategory.contentMetadataAndCatalogData, () => "Content Metadata & Catalog Data")
    .with(ListingSubCategory.oddsAndBettingData, () => "Odds & Betting Data")
    .exhaustive();
//# sourceMappingURL=listing-utils.js.map